import type { RusadaFlightToTechlogContextType } from './types';

/**
 * Default transformation schema for mapping Rusada flights to TechLog sectors
 *
 * Maps Rusada flight data structure to TechLog sector creation input format.
 * A sector in TechLog represents a single flight leg.
 *
 * Migrated from: src/transmormer/schema/rusadaFlightToTechlogDefaultSchema.ts
 */
const rusadaFlightToTechlogSchema = {
  // Map to existing trip or create new trip reference
  tripId: (input: RusadaFlightToTechlogContextType) => {
    // Use a combination of flight date and registration to group flights into trips
    const flightDate = input.rusada?.flight?.flightDate;
    const registration = input.rusada?.flight?.flightRegistrationDescription;

    if (flightDate && registration) {
      // Create a trip identifier that groups flights by date and aircraft
      const dateStr = new Date(flightDate).toISOString().split('T')[0];
      return `rusada-trip-${registration}-${dateStr}`;
    }

    // Fallback to flight-specific trip
    return `rusada-flight-trip-${input.rusada?.flight?.id}`;
  },

  // Map departure airport - will need airport lookup resolver
  departureAirportId: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.airports?.departure?.techlogId ||
    input.rusada?.flight?.departurePlaceId,

  // Map arrival airport - will need airport lookup resolver
  arrivalAirportId: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.airports?.arrival?.techlogId ||
    input.rusada?.flight?.arrivalPlaceId,

  // Handle diverted flights
  actualArrivalAirportId: (input: RusadaFlightToTechlogContextType) => {
    const divertedPlaceId = input.rusada?.flight?.divertedPlaceId;
    if (divertedPlaceId && divertedPlaceId !== 0) {
      return input.rusada?.airports?.diverted?.techlogId || divertedPlaceId;
    }
    return undefined;
  },

  // Map takeoff time from departure actual or takeoff time
  takeoffTime: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.flight?.departureTakeOff ||
    input.rusada?.flight?.departureActual,

  // Map landing time from arrival landed or actual
  landingTime: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.flight?.arrivalLanded || input.rusada?.flight?.arrivalActual,

  // Use departure actual time for off blocks if available
  offblocksTime: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.flight?.departureActual,

  // Use arrival actual time for on blocks if available
  onblocksTime: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.flight?.arrivalActual,

  // Set landing count to 1 for completed flights
  landingsCount: (input: RusadaFlightToTechlogContextType) => 1,

  // Map flight type from flight model or default to training
  flightType: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.flight?.flightModelDescription || 'Training',

  // Store external flight ID for reference
  flightId: (input: RusadaFlightToTechlogContextType) =>
    `rusada-${input.rusada?.flight?.id}`,

  // Store custom data for sync tracking
  preflightCustomData: (input: RusadaFlightToTechlogContextType) => ({
    rusada: {
      flightId: input.rusada?.flight?.id,
      flightStatus: input.rusada?.flight?.flightStatusDescription,
      flightModelDescription: input.rusada?.flight?.flightModelDescription,
      flightLineDescription: input.rusada?.flight?.flightLineDescription,
      flightTypeDescription: input.rusada?.flight?.flightTypeDescription,
      flightNumberDescription: input.rusada?.flight?.flightNumberDescription,
      customerDescription: input.rusada?.flight?.customerDescription,
      divertedReason: input.rusada?.flight?.divertedReason,
      abortCancelCodeDescription:
        input.rusada?.flight?.abortCancelCodeDescription,
      plannedFlightTime: input.rusada?.flight?.plannedFlightTime,
    },
    sync: {
      sourceSystem: 'RUSADA',
      sourceEntityId: input.rusada?.flight?.id,
      sourceEntityType: 'FLIGHT',
      lastSyncAt: new Date().toISOString(),
    },
  }),

  // Set pilot information if available
  pilotInCommandId: (input: RusadaFlightToTechlogContextType) => {
    // Would need pilot lookup based on crew data
    // For now, return undefined as this requires additional context
    return undefined;
  },

  // Set user ID based on context user
  userId: (input: RusadaFlightToTechlogContextType) => {
    // Would need user mapping between systems
    // For now, use a default system user
    return undefined;
  },

  // Set departure date based on flight date
  date: (input: RusadaFlightToTechlogContextType) =>
    input.rusada?.flight?.flightDate,

  // Set source as RUSADA
  source: (input: RusadaFlightToTechlogContextType) => 'RUSADA',

  // Set FRAT score if available
  fratScore: (input: RusadaFlightToTechlogContextType) => {
    // No direct mapping in Rusada - could calculate based on flight conditions
    return undefined;
  },

  // Map fuel burn if available
  plannedFuelBurn: (input: RusadaFlightToTechlogContextType) => {
    // No direct mapping in Rusada data
    return undefined;
  },

  actualFuelBurn: (input: RusadaFlightToTechlogContextType) => {
    // No direct mapping in Rusada data
    return undefined;
  },

  // Set remarks with flight details
  remarks: (input: RusadaFlightToTechlogContextType) => {
    const flight = input.rusada?.flight;
    if (!flight) return 'Rusada flight import';

    const remarks = [];
    if (flight.flightNumberDescription) {
      remarks.push(`Flight: ${flight.flightNumberDescription}`);
    }
    if (flight.customerDescription) {
      remarks.push(`Customer: ${flight.customerDescription}`);
    }
    if (flight.divertedReason) {
      remarks.push(`Diversion: ${flight.divertedReason}`);
    }
    if (flight.abortCancelCodeDescription) {
      remarks.push(`Status: ${flight.abortCancelCodeDescription}`);
    }

    return remarks.length > 0 ? remarks.join(' | ') : 'Rusada flight import';
  },
};

export default rusadaFlightToTechlogSchema;
